import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LiveKitRoom,
  VideoConference,
  RoomAudioRenderer,
  useRoomContext,
} from '@livekit/components-react';
import { RoomEvent, ConnectionState, Track } from 'livekit-client';
import '@livekit/components-styles';
/* ─── LiveKit control-bar visibility overrides ─────────────────────────────
 * Ensure the mic/camera/share/chat/leave buttons are always clearly visible
 * regardless of the host app's background colour.
 * These override the [data-lk-theme="default"] CSS variables where needed.
 * ───────────────────────────────────────────────────────────────────────── */
import './livekit-overrides.css';
import Editor from '@monaco-editor/react';
import * as Y from 'yjs';
import { MonacoBinding } from 'y-monaco';

import { getSession, endSession, getLiveKitToken, getHostToken, releaseAgentBinding } from '../api/sessions';
import { getSnapshot, saveSnapshot, executeCode } from '../api/code';
import { authStore } from '../api/client';
import { formatTime, severityColor } from '../utils/format';

// Phase 1 - session-side panels
import QuestionsTab            from '../components/session/QuestionsTab';
import NotesTab                from '../components/session/NotesTab';
import ScorecardTab            from '../components/session/ScorecardTab';
import CandidateReadinessCard  from '../components/session/CandidateReadinessCard';
import { IntegrityStatusBadge } from '../components/IntegrityStatusBadge';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

import { getWsBaseUrl } from '../config/env';

const WS_URL           = getWsBaseUrl();
const LIVEKIT_URL      = import.meta.env.VITE_LIVEKIT_URL || '';
const MAX_FINDINGS     = 500;
/** Dashboard WS reconnect backoff after an unexpected disconnect (transport blip / proxy idle). */
const WS_RECONNECT_MS  = 3000;
/** Throttle WS resync triggered by candidate_joined (avoids rapid reconnect bursts). */
const WS_RESYNC_GAP_MS = 800;

/** Backend snapshot / relay messages vary by field naming; normalise presence here. */
function readOptionalBool(v) {
  if (v === undefined || v === null) return undefined;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const s = v.toLowerCase().trim();
    if (['true', 'yes', '1', 'online', 'connected'].includes(s)) return true;
    if (['false', 'no', '0', 'offline', 'disconnected'].includes(s)) return false;
  }
  if (typeof v === 'number') return v !== 0;
  return undefined;
}

/**
 * @returns {boolean|undefined} undefined if the message does not describe agent presence
 */
/** @returns {boolean|undefined} */
/** Phase 12 snapshot: duplicates fields flat and under payload - merge before reading agent/findings/risk. */
function mergeRelaySnapshot(msg) {
  const p = msg?.payload && typeof msg.payload === 'object' ? msg.payload : {};
  return { ...p, ...msg };
}

function deriveScanActiveFromRelayMessage(msg) {
  if (!msg || typeof msg !== 'object') return undefined;
  const flag = readOptionalBool(
    msg.scanActive ?? msg.scan_active ?? msg.scanRunning ?? msg.scan_running,
  );
  if (flag !== undefined) return flag;
  const st = `${msg.scanStatus ?? msg.scan_status ?? ''}`.toLowerCase().trim();
  if (['running', 'active', 'on'].includes(st)) return true;
  if (['stopped', 'idle', 'off', 'inactive'].includes(st)) return false;
  return undefined;
}

function deriveAgentOnlineFromRelayMessage(msg) {
  if (!msg || typeof msg !== 'object') return undefined;
  const candidates = [
    msg.agentOnline,
    msg.agent_online,
    msg.isAgentOnline,
    msg.agentConnected,
    msg.agent_connected,
    msg.desktopAgentOnline,
    msg.detectionAgentOnline,
    msg.agentPresent,
    msg?.state?.agentOnline,
    msg?.state?.agent_connected,
    msg?.snapshot?.agentOnline,
    msg?.payload?.agentOnline,
    msg?.payload?.agent_online,
    msg?.data?.agentOnline,
    msg?.detection?.agentOnline,
    msg?.presence?.agentOnline,
  ];
  for (const c of candidates) {
    const b = readOptionalBool(c);
    if (b !== undefined) return b;
  }
  const nAgents = msg.connectedAgents ?? msg.connected_agents ?? msg.agentConnectionCount;
  if (typeof nAgents === 'number' && Number.isFinite(nAgents) && nAgents > 0) return true;
  const status = (msg.agentStatus || msg.agent_status || msg.agentWsStatus || '').toString().toLowerCase();
  if (status === 'connected' || status === 'online') return true;
  if (status === 'disconnected' || status === 'offline') return false;
  return undefined;
}

/** Default starter code shown when no snapshot exists yet. */
const STARTER_CODE = {
  python:     '# Write your solution here\n',
  javascript: '// Write your solution here\n',
  java:       '// Write your solution here\npublic class Solution {\n\n}\n',
  cpp:        '// Write your solution here\n#include <iostream>\nusing namespace std;\n\nint main() {\n\n}\n',
  c:          '// Write your solution here\n#include <stdio.h>\n\nint main() {\n\n}\n',
};

/** Language options for the toolbar selector (monaco language ID === value). */
const LANGUAGE_OPTIONS = [
  { value: 'python',     label: 'Python' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'java',       label: 'Java' },
  { value: 'cpp',        label: 'C++' },
  { value: 'c',          label: 'C' },
];

// ---------------------------------------------------------------------------
// LiveKit error boundary
// Catches runtime errors thrown by @livekit/components-react so they don't
// crash the entire page (e.g. misconfigured server URL in local dev).
// ---------------------------------------------------------------------------

class LiveKitErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || 'LiveKit error' };
  }

  componentDidCatch(error) {
    console.error('[Dashboard] LiveKit error boundary caught:', error.message);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center gap-3
                        bg-white/5 rounded-xl m-4 border border-white/10">
          <span className="text-4xl" aria-hidden="true">📹</span>
          <p className="text-slate-300 text-sm font-medium">Video unavailable in this environment</p>
          <p className="text-slate-500 text-xs max-w-xs text-center">{this.state.message}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// MediaEnabler
// Inner LiveKit child that explicitly publishes mic + camera tracks once the
// room reaches the "connected" state. The declarative `audio`/`video` props
// on `<LiveKitRoom>` are unreliable across browsers in components-react@2.x
// (Chrome on some hosts silently keeps tracks muted; Safari/Firefox often
// require an explicit setMicrophoneEnabled/setCameraEnabled call). The
// `onConnected` prop is parameter-less in v2.x, so we cannot enable tracks
// from there. Doing it here via useRoomContext + RoomEvent.Connected works
// reliably and also handles auto-reconnects.
// ---------------------------------------------------------------------------

function MediaEnabler() {
  const room = useRoomContext();
  const [permissionError, setPermissionError] = useState(null);

  useEffect(() => {
    if (!room) return undefined;

    let cancelled = false;

    const enableTracks = async () => {
      const lp = room.localParticipant;
      if (!lp || cancelled) return;

      const hasMic = [...lp.trackPublications.values()].some(
        (p) => p?.track && p.source === Track.Source.Microphone,
      );
      const hasCam = [...lp.trackPublications.values()].some(
        (p) => p?.track && p.source === Track.Source.Camera,
      );
      if (hasMic && hasCam) {
        console.log('[Dashboard] mic+camera already published - skipping duplicate enable');
        return;
      }

      console.log('[Dashboard] enabling mic + camera...');

      // Pre-flight check: if Permissions-Policy blocks the API the browser
      // throws synchronously with NotAllowedError before showing any prompt.
      // Probe with a plain getUserMedia first so we can surface the right
      // remediation instead of the cryptic LiveKit stack trace.
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true,
        });
        // Stop the probe stream immediately - LiveKit will create its own.
        stream.getTracks().forEach((t) => t.stop());
        if (!cancelled) setPermissionError(null);
      } catch (e) {
        const name = e?.name || 'Error';
        const msg = e?.message || String(e);
        console.error('[Dashboard] getUserMedia probe failed:', name, msg);
        if (!cancelled) {
          if (msg.toLowerCase().includes('permissions policy') ||
              msg.toLowerCase().includes('permission policy')) {
            setPermissionError(
              'This page is not allowed to use the camera/microphone ' +
              '(Permissions-Policy header blocks it). Reload the page after ' +
              'the latest deploy is live, or open the site in an incognito ' +
              'window to clear cached headers.'
            );
          } else if (name === 'NotAllowedError') {
            setPermissionError(
              'Camera/microphone access was denied. Click the camera icon in ' +
              'the address bar, choose "Always allow", and reload the page.'
            );
          } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
            setPermissionError(
              'No camera or microphone was found on this device.'
            );
          } else if (name === 'NotReadableError') {
            setPermissionError(
              'Camera or microphone is being used by another application. ' +
              'Close other video apps and try again.'
            );
          } else {
            setPermissionError(`Unable to access camera/microphone: ${msg}`);
          }
        }
        // Skip LiveKit publish attempts - they will throw the same error.
        return;
      }

      try {
        await lp.setMicrophoneEnabled(true);
        if (!cancelled) console.log('[Dashboard] microphone enabled');
      } catch (e) {
        console.error('[Dashboard] mic enable failed:', e?.name, e?.message);
      }
      try {
        await lp.setCameraEnabled(true);
        if (!cancelled) console.log('[Dashboard] camera enabled');
      } catch (e) {
        console.error('[Dashboard] camera enable failed:', e?.name, e?.message);
      }
    };

    let publishTimer = null;
    const schedulePublish = () => {
      clearTimeout(publishTimer);
      // Let the RTC engine finish connecting; parallel publish with <LiveKitRoom audio/video>
      // causes "engine not connected within timeout" on some reload / StrictMode paths.
      publishTimer = setTimeout(() => {
        if (!cancelled) enableTracks();
      }, 200);
    };

    // Already connected (e.g. tab switch back into the video panel)
    if (room.state === ConnectionState.Connected) {
      schedulePublish();
    }

    const handleConnected = () => schedulePublish();
    room.on(RoomEvent.Connected, handleConnected);
    room.on(RoomEvent.Reconnected, handleConnected);

    return () => {
      cancelled = true;
      clearTimeout(publishTimer);
      room.off(RoomEvent.Connected, handleConnected);
      room.off(RoomEvent.Reconnected, handleConnected);
    };
  }, [room]);

  if (!permissionError) return null;

  return (
    <div
      role="alert"
      style={{
        position: 'absolute',
        top: 12,
        left: 12,
        right: 12,
        zIndex: 30,
        background: '#7f1d1d',
        color: '#fee2e2',
        border: '1px solid #b91c1c',
        borderRadius: 8,
        padding: '10px 14px',
        fontSize: 13,
        lineHeight: 1.4,
        boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
      }}
    >
      <strong style={{ display: 'block', marginBottom: 4 }}>
        Camera / microphone unavailable
      </strong>
      <span>{permissionError}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Findings panel sub-components
// ---------------------------------------------------------------------------

/** Single finding card in the right panel. */
function FindingCard({ finding }) {
  // I-P0-B: every field here is the SERVER classification (severity, category, message).
  const sev = (finding.severity || 'LOW').toUpperCase();
  const summary = finding.message || '';
  const tag = finding.resolved
    ? 'Resolved'
    : finding.reviewRequired ? 'Needs review' : 'Context only';

  return (
    <div className="px-3 py-2.5 border-b border-white/5 hover:bg-white/5 transition-colors">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px]
                          font-bold ${severityColor(sev)}`}>
          {sev}
        </span>
        <span className="text-[9px] uppercase tracking-wider text-slate-500">
          {finding.integrityCategory ? `${finding.integrityCategory} · ` : ''}{tag}
        </span>
        <span className="text-[10px] text-slate-500 font-mono shrink-0">
          {formatTime(finding.detectedAt ?? finding.createdAt)}
        </span>
      </div>
      {(finding.processName || finding.type) && (
        <p className="text-xs font-semibold text-white mb-0.5 truncate">
          {finding.processName || finding.type}
        </p>
      )}
      {summary && (
        <p className="text-[10px] text-slate-400" title={summary}>{summary}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

/**
 * Dashboard - Live session monitoring page.
 *
 * Route: /session/:sessionId  (mounted via SessionPage.jsx, which guards auth)
 * useParams() is called directly here - SessionPage no longer builds the session
 * object; it only guards against a missing token.
 *
 * State:
 *   session       - full session object from getSession()
 *   livekitToken  - string token from getLiveKitToken()
 *   livekitUrl    - server URL resolved from session or env var
 *   findings      - array (newest first, max 500)
 *   isConnected   - agent WebSocket presence
 *   scanActive    - whether the agent scanner is running
 *   loading       - initial parallel fetch in-flight
 *   error         - fetch error string
 *   ending        - endSession() call in-flight
 *
 * @param {object} props
 * @param {Function} [props.onEndSession]  optional callback used by SessionPage
 */
export function Dashboard({ onEndSession }) {
  const { sessionId } = useParams();
  const navigate       = useNavigate();

  // Session / LiveKit state
  const [session,      setSession]      = useState(null);
  const [livekitToken, setLivekitToken] = useState(null);
  const [livekitUrl,   setLivekitUrl]   = useState('');

  // Detection state
  const [findings,     setFindings]     = useState([]);
  const [isConnected,  setIsConnected]  = useState(false);
  const [scanActive,   setScanActive]   = useState(false);

  // Page-level state
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [ending,       setEnding]       = useState(false);

  // Tab state
  const [activeTab,    setActiveTab]    = useState('video');

  // Phase 1 - readiness + change token (bumped on `question_changed` WS events)
  const [readiness,    setReadiness]        = useState(null);
  const [questionChangeToken, setQuestionChangeToken] = useState(0);

  // Phase 3 - risk score timeline
  // I-P0-B: server integrity status (NONE / MEDIUM / HIGH). No numeric risk score.
  const [integrityStatus, setIntegrityStatus] = useState(null);
  // I-P0-C: integrity-monitoring channel state (technical, never a candidate signal).
  const [monitoring, setMonitoring] = useState(null);   // null | 'INTERRUPTED' | 'RESTORED'
  const [agentHealth, setAgentHealth] = useState(null);
  const [releasing, setReleasing] = useState(false);

  // Code editor state
  const [content,        setContent]        = useState('');
  const [language,       setLanguage]       = useState('python');
  const [executing,      setExecuting]      = useState(false);
  const [execResult,     setExecResult]     = useState(null);
  const [outputOpen,     setOutputOpen]     = useState(false);
  const [lastSavedAt,    setLastSavedAt]    = useState(null);
  const [lastSavedBy,    setLastSavedBy]    = useState(null);
  const [snapshotLoaded, setSnapshotLoaded] = useState(false);
  const [saveSuccess,    setSaveSuccess]    = useState(false);

  // Refs
  const wsRef              = useRef(null);
  const mountedRef         = useRef(true);
  const isEditorFocusedRef = useRef(false);
  const debounceTimerRef   = useRef(null);
  const monacoEditorRef    = useRef(null);
  const contentRef         = useRef('');
  const languageRef        = useRef('python');

  // Yjs CRDT refs - one shared Y.Doc per dashboard instance.
  // Replaces the old fire-and-forget code_update content relay so that the
  // host and candidate can type concurrently without overwriting each other.
  const ydocRef            = useRef(null);
  const ytextRef           = useRef(null);
  const monacoBindingRef   = useRef(null);
  const yjsBoundRef        = useRef(false);
  const pendingYjsRef      = useRef([]);   // b64 updates that arrive before bind

  // Dashboard WebSocket reconnect + intentional-close suppression
  const wsCredsRef              = useRef(null);
  const wsReconnectTimerRef     = useRef(null);
  const wsReconnectSuppressedRef = useRef(false);
  const openWsLatestRef           = useRef(null);
  const lastWsResyncAtRef        = useRef(0);
  /** Dev-only: log snapshot shape once per socket to avoid console spam. */
  const snapshotPresenceDebugRef = useRef(false);

  // ---------------------------------------------------------------------------
  // WebSocket connection (opened after session is loaded)
  // Backend: GET /ws/dashboard?token=<host JWT only> - sessionId comes from token claims.
  // ---------------------------------------------------------------------------

  const openWs = useCallback((hostToken) => {
    if (!hostToken) {
      console.warn('[Dashboard] openWs skipped - missing hostToken');
      return;
    }

    wsCredsRef.current = { hostToken };

    clearTimeout(wsReconnectTimerRef.current);
    wsReconnectTimerRef.current = null;

    const prev = wsRef.current;
    if (prev) {
      prev.onclose = null;
      try { prev.close(); } catch { /* ignore */ }
      wsRef.current = null;
    }

    const url = `${WS_URL}/ws/dashboard?token=${encodeURIComponent(hostToken)}`;
    console.log('[Dashboard] opening WS:', url.replace(hostToken, '[REDACTED]'));

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current) return;
      snapshotPresenceDebugRef.current = false;
      console.log('[Dashboard] WS connected - awaiting server snapshot push');
    };

    ws.onclose = () => {
      wsRef.current = null;
      if (!mountedRef.current || wsReconnectSuppressedRef.current) return;
      console.log('[Dashboard] WS closed unexpectedly - reconnect scheduled');
      // Do NOT flip agent/offline here - the socket closing is unrelated to desktop agent presence.
      clearTimeout(wsReconnectTimerRef.current);
      wsReconnectTimerRef.current = setTimeout(() => {
        wsReconnectTimerRef.current = null;
        const c = wsCredsRef.current;
        if (mountedRef.current && !wsReconnectSuppressedRef.current && c?.hostToken) {
          openWsLatestRef.current?.(c.hostToken);
        }
      }, WS_RECONNECT_MS);
    };

    ws.onerror = (e) => console.error('[Dashboard] WS error', e);

    ws.onmessage = (evt) => {
      if (!mountedRef.current) return;
      let msg;
      try {
        msg = JSON.parse(evt.data);
      } catch {
        console.warn('[Dashboard] WS non-JSON message ignored');
        return;
      }

      console.log('[Dashboard] WS event:', msg.type || msg.event);

      switch (msg.type || msg.event) {
        case 'agent_connected':
          setIsConnected(true);
          break;

        case 'agent_disconnected':
          setIsConnected(false);
          setScanActive(false);
          break;

        // Candidate joined: update the session info panel immediately without a page reload.
        case 'candidate_joined':
          console.log('[Dashboard] candidate_joined - email:', msg.candidateEmail);
          setSession(prev => prev
            ? { ...prev, candidateEmail: msg.candidateEmail || prev.candidateEmail }
            : prev
          );
          // Re-open the relay so we reliably receive snapshot + agent_connected after reconnects,
          // without treating a transport drop as "agent offline".
          queueMicrotask(() => {
            if (!mountedRef.current || wsReconnectSuppressedRef.current) return;
            const now = Date.now();
            if (now - lastWsResyncAtRef.current < WS_RESYNC_GAP_MS) return;
            lastWsResyncAtRef.current = now;
            const c = wsCredsRef.current;
            if (c?.hostToken) openWsLatestRef.current?.(c.hostToken);
          });
          break;

        case 'finding': {
          setIsConnected(true);
          const f = msg.finding ?? msg;
          // The server already dedupes one condition into one row; keep every row it sends.
          setFindings((prev) => {
            const filtered = f?.id ? prev.filter((x) => x?.id !== f.id) : prev;
            return [f, ...filtered].slice(0, MAX_FINDINGS);
          });
          break;
        }

        case 'finding:resolved':
          // Keep the record; mark the condition as resolved (it still happened).
          setFindings((prev) =>
            prev.map((x) => (x.id === msg.findingId ? { ...x, resolved: true } : x))
          );
          break;

        case 'scan_started':
        case 'scan_start':
          console.log('[Dashboard] scan started');
          setScanActive(true);
          setIsConnected(true);
          break;

        case 'scan_stopped':
        case 'scan_stop':
          console.log('[Dashboard] scan stopped');
          setScanActive(false);
          break;

        // Backend relay broadcasts {event: 'scan_status', status: 'running'|'stopped'}
        case 'scan_status': {
          const running = (msg.status || '').toLowerCase() === 'running';
          console.log('[Dashboard] scan_status →', msg.status, '(active:', running, ')');
          setScanActive(running);
          if (running) setIsConnected(true);
          break;
        }

        case 'snapshot':
        case 'detection_snapshot':
        case 'state_snapshot': {
          const snap = mergeRelaySnapshot(msg);
          const agent = deriveAgentOnlineFromRelayMessage(snap);
          if (agent !== undefined) setIsConnected(agent);
          if (
            import.meta.env.DEV &&
            agent === undefined &&
            !snapshotPresenceDebugRef.current
          ) {
            snapshotPresenceDebugRef.current = true;
            console.debug(
              '[Dashboard] snapshot agent presence unclear; envelope keys:',
              Object.keys(msg),
              'merged keys:',
              Object.keys(snap),
            );
          }
          const scanOn = deriveScanActiveFromRelayMessage(snap);
          if (scanOn !== undefined) setScanActive(scanOn);
          if (Array.isArray(snap.findings) && snap.findings.length > 0) {
            setFindings([...snap.findings].slice(0, MAX_FINDINGS));
          }
          if (snap.integrityStatus != null) {
            setIntegrityStatus(snap.integrityStatus);
          }
          break;
        }

        case 'pong':
          break;

        case 'error':
          console.warn('[Dashboard] relay error event:', msg.message || msg.reason || msg);
          break;

        case 'integrity_status': {
          setIsConnected(true);
          if (msg.status) setIntegrityStatus(msg.status);
          break;
        }

        case 'risk_score_update':
          // I-P0-B: numeric risk scores are retired; ignore any legacy broadcast.
          break;

        // I-P0-C: the server lost / regained the agent heartbeat. Neutral technical state.
        case 'monitoring_status':
          setMonitoring(msg.status || null);
          break;

        case 'agent_health':
          setAgentHealth({ detectors: msg.detectors || {}, agentVersion: msg.agentVersion, lastSeenAt: msg.lastSeenAt });
          setMonitoring((prev) => (prev === 'INTERRUPTED' ? 'RESTORED' : prev));
          break;

        case 'code_update': {
          // CONTENT is now synced via Yjs (case 'yjs_update'). This branch
          // only honours language switches so the syntax highlighter follows
          // whatever language the candidate picked.
          const update = msg.code ?? msg.snapshot ?? msg;
          const who    = (msg.updatedBy || update.updatedBy || '').toLowerCase();
          if (who === 'host') break;
          const newLanguage = update.language;
          if (newLanguage && newLanguage !== languageRef.current) {
            console.log('[Dashboard] language synced from candidate ->', newLanguage);
            setLanguage(newLanguage);
            languageRef.current = newLanguage;
          }
          break;
        }

        // Yjs CRDT binary update (Base64). Apply with origin='remote' so the
        // doc.on('update') handler does not echo it back to the network.
        case 'yjs_update': {
          const b64 = msg.content;
          if (!b64) break;
          const ydoc = ydocRef.current;
          if (!ydoc) {
            pendingYjsRef.current.push(b64);
            console.log('[Dashboard] yjs_update queued (binding not ready)');
            break;
          }
          try {
            const bin = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
            Y.applyUpdate(ydoc, bin, 'remote');
          } catch (err) {
            console.error('[Dashboard] yjs_update apply failed:', err.message);
          }
          break;
        }

        // Phase 1 - host gets notified when a question is asked/answered/scored
        case 'question_changed':
          setQuestionChangeToken((t) => t + 1);
          break;

        // Phase 1 - desktop agent reports candidate environment readiness
        case 'readiness_update':
          if (msg.readiness && typeof msg.readiness === 'object') {
            setReadiness(msg.readiness);
            setIsConnected(true);
          }
          break;

        default: {
          const inferred = deriveAgentOnlineFromRelayMessage(mergeRelaySnapshot(msg));
          if (inferred !== undefined) setIsConnected(inferred);
          break;
        }
      }
    };
  }, []);

  openWsLatestRef.current = openWs;

  // ---------------------------------------------------------------------------
  // Send a command to the agent via the WS relay
  // ---------------------------------------------------------------------------

  const sendWsCmd = useCallback((event) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ event }));
      console.log('[Dashboard] sent WS command:', event);
    } else {
      console.warn('[Dashboard] WS not open - cannot send:', event);
    }
  }, []);

  // Send a full WS payload (used for live code_update streaming to the agent).
  const sendWsPayload = useCallback((payload) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(payload));
    }
  }, []);

  const handleStartScan = useCallback(() => {
    console.log('[Dashboard] Start Scan clicked → optimistic UI on');
    setScanActive(true);            // optimistic - flip to Stop Scan immediately
    sendWsCmd('cmd_start_scan');
  }, [sendWsCmd]);
  const handleStopScan = useCallback(() => {
    console.log('[Dashboard] Stop Scan clicked → optimistic UI off');
    setScanActive(false);           // optimistic - flip to Start Scan immediately
    sendWsCmd('cmd_stop_scan');
  }, [sendWsCmd]);

  // Clear findings + reset risk locally and on the agent (relay broadcasts to
  // every dashboard tab so they all wipe in lockstep).
  const handleClearFindings = useCallback(() => {
    console.log('[Dashboard] Clear Findings clicked');
    sendWsCmd('cmd_clear_alerts');
    setFindings([]);
  }, [sendWsCmd]);

  // Keep contentRef + languageRef in sync for use inside stale closures
  useEffect(() => { contentRef.current  = content;  }, [content]);
  useEffect(() => { languageRef.current = language; }, [language]);

  // ---------------------------------------------------------------------------
  // Code editor: debounced auto-save (1.5s after last keystroke)
  // ---------------------------------------------------------------------------

  const debouncedSave = useCallback((val, lang) => {
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      if (!mountedRef.current) return;
      try {
        await saveSnapshot(sessionId, { language: lang, content: val });
        if (mountedRef.current) {
          setLastSavedAt(new Date());
          console.log('[Dashboard] auto-save successful - language:', lang);
        }
      } catch (err) {
        console.error('[Dashboard] auto-save failed:', err.message);
      }
    }, 1500);
  }, [sessionId]);

  /** Immediate save triggered by the Save button - cancels any pending debounce. */
  const handleSave = useCallback(async () => {
    clearTimeout(debounceTimerRef.current);
    try {
      await saveSnapshot(sessionId, {
        language: languageRef.current,
        content:  contentRef.current,
      });
      setLastSavedAt(new Date());
      setSaveSuccess(true);
      setTimeout(() => { if (mountedRef.current) setSaveSuccess(false); }, 2000);
      console.log('[Dashboard] manual save successful');
    } catch (err) {
      console.error('[Dashboard] manual save failed:', err.message);
    }
  }, [sessionId]);

  /** Execute the current code via the backend sandbox. */
  const handleRun = useCallback(async () => {
    setExecuting(true);
    setOutputOpen(true);
    const lang = languageRef.current;
    const code = contentRef.current;
    console.log('[Dashboard] executing code - language:', lang, 'chars:', code.length);
    try {
      const res    = await executeCode(sessionId, { language: lang, sourceCode: code, stdin: '' });
      const result = res.data;
      console.log('[Dashboard] execution result - status:', result.status);
      if (mountedRef.current) setExecResult(result);
    } catch (err) {
      console.error('[Dashboard] executeCode failed:', err.message);
      if (mountedRef.current) {
        setExecResult({
          stdout:        '',
          stderr:        err.response?.data?.message || err.message || 'Execution request failed.',
          compileOutput: '',
          status:        'ERROR',
          time:          null,
          memory:        null,
        });
      }
    } finally {
      if (mountedRef.current) setExecuting(false);
    }
  }, [sessionId]);

  /** Handle language selector change - updates state and triggers debounced save. */
  const handleLanguageChange = useCallback((e) => {
    const newLang = e.target.value;
    setLanguage(newLang);
    languageRef.current = newLang;
    debouncedSave(contentRef.current, newLang);
    console.log('[Dashboard] language changed to:', newLang);
  }, [debouncedSave]);

  /** Monaco Editor onMount - wire focus tracking + store editor instance ref. */
  const handleEditorMount = useCallback((editor) => {
    monacoEditorRef.current = editor;
    editor.onDidFocusEditorText(() => {
      isEditorFocusedRef.current = true;
      console.log('[Dashboard] Monaco editor focused');
    });
    editor.onDidBlurEditorText(() => {
      isEditorFocusedRef.current = false;
      console.log('[Dashboard] Monaco editor blurred');
    });
    console.log('[Dashboard] Monaco editor mounted');
  }, []);

  // ---------------------------------------------------------------------------
  // Load code snapshot when the Code tab is opened for the first time
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (activeTab !== 'code' || snapshotLoaded || !sessionId) return;
    setSnapshotLoaded(true);

    async function loadSnapshot() {
      console.log('[Dashboard] loading code snapshot for session:', sessionId);
      try {
        const res  = await getSnapshot(sessionId);
        const snap = res.data;
        const lang = snap.language || 'python';
        console.log('[Dashboard] snapshot loaded - language:', lang, 'chars:', snap.content?.length ?? 0);
        setLanguage(lang);
        languageRef.current = lang;
        const text = snap.content || STARTER_CODE[lang] || STARTER_CODE.python;
        setContent(text);
        contentRef.current = text;
        setLastSavedAt(snap.updatedAt ? new Date(snap.updatedAt) : null);
        setLastSavedBy(snap.updatedBy ?? snap.authorName ?? snap.candidateName ?? null);
      } catch (err) {
        if (err.response?.status === 404) {
          console.log('[Dashboard] no snapshot yet - initialising with starter code');
          const starter = STARTER_CODE[languageRef.current] || STARTER_CODE.python;
          setContent(starter);
          contentRef.current = starter;
        } else {
          console.error('[Dashboard] getSnapshot failed:', err.message);
        }
      }
    }

    loadSnapshot();
  }, [activeTab, snapshotLoaded, sessionId]);

  // ---------------------------------------------------------------------------
  // Yjs binding: created once Monaco is mounted AND the snapshot has loaded.
  // The dashboard seeds the shared Y.Text with the snapshot content so the
  // agent (which starts blank) receives it as a normal Yjs update.
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!snapshotLoaded || !monacoEditorRef.current || yjsBoundRef.current) return;

    const editor = monacoEditorRef.current;
    const model  = editor.getModel();
    if (!model) return;

    console.log('[Dashboard] Yjs - creating Y.Doc and binding to Monaco');

    const ydoc  = new Y.Doc();
    const ytext = ydoc.getText('monaco');

    // Seed the shared text with the snapshot already in the model.
    // The resulting update will broadcast to the candidate so they pick it up.
    const initial = model.getValue();
    if (initial && initial.length > 0) {
      ytext.insert(0, initial);
      console.log('[Dashboard] Yjs - seeded ytext from snapshot (', initial.length, 'chars)');
    }

    // Outbound: every local update -> Base64 -> WS -> backend -> agent.
    ydoc.on('update', (update, origin) => {
      if (origin === 'remote') return;
      try {
        let s = '';
        for (let i = 0; i < update.length; i++) s += String.fromCharCode(update[i]);
        const b64 = btoa(s);
        sendWsPayload({ event: 'yjs_update', content: b64 });
      } catch (err) {
        console.error('[Dashboard] Yjs outbound update failed:', err.message);
      }
    });

    // Bind Yjs <-> Monaco. MonacoBinding mutates the model via real edits so
    // the local cursor position is preserved across remote updates.
    const binding = new MonacoBinding(ytext, model, new Set([editor]));

    ydocRef.current          = ydoc;
    ytextRef.current         = ytext;
    monacoBindingRef.current = binding;
    yjsBoundRef.current      = true;

    // Drain any yjs_update messages that arrived before the binding existed.
    if (pendingYjsRef.current.length > 0) {
      console.log('[Dashboard] Yjs - draining', pendingYjsRef.current.length, 'queued updates');
      const queued = pendingYjsRef.current.splice(0);
      for (const b64 of queued) {
        try {
          const bin = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
          Y.applyUpdate(ydoc, bin, 'remote');
        } catch (err) {
          console.error('[Dashboard] Yjs queued apply failed:', err.message);
        }
      }
    }

    return () => {
      try { binding.destroy(); } catch { /* ignore */ }
      try { ydoc.destroy();    } catch { /* ignore */ }
      ydocRef.current          = null;
      ytextRef.current         = null;
      monacoBindingRef.current = null;
      yjsBoundRef.current      = false;
      console.log('[Dashboard] Yjs - binding destroyed');
    };
  }, [snapshotLoaded, sendWsPayload]);

  // ---------------------------------------------------------------------------
  // Monaco layout refresh when switching to the Code tab
  // The editor needs a forced re-measure after its container un-hides.
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (activeTab === 'code' && monacoEditorRef.current) {
      const frame = requestAnimationFrame(() => {
        monacoEditorRef.current?.layout();
        console.log('[Dashboard] Monaco layout refreshed on tab switch');
      });
      return () => cancelAnimationFrame(frame);
    }
  }, [activeTab]);

  // ---------------------------------------------------------------------------
  // Initial data load: getSession + getLiveKitToken (parallel)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    wsReconnectSuppressedRef.current = false;
    mountedRef.current = true;
    let cancelled = false;

    console.log('[Dashboard] mount - loading session', sessionId);

    async function load() {
      try {
        // 1. Fetch session details
        const sessionRes = await getSession(sessionId);
        if (cancelled) return;

        const s = sessionRes.data;
        console.log('[Dashboard] session loaded - code:', s.sessionCode ?? s.code, 'status:', s.status);
        setSession(s);
        if (s.candidateReadiness && Object.keys(s.candidateReadiness).length > 0) {
          setReadiness(s.candidateReadiness);
        }

        // 2. Resolve LiveKit URL (session field > env var)
        const lkUrl = s.livekitUrl || LIVEKIT_URL;
        setLivekitUrl(lkUrl);

        // 3. Fetch LiveKit token using session code
        const code = s.sessionCode ?? s.code;
        if (code && authStore.accessToken) {
          try {
            const embeddedHostToken =
              s.livekitHostToken ||
              s.hostLivekitToken ||
              s.hostLiveKitToken ||
              s.interviewerLivekitToken ||
              s.interviewerLiveKitToken ||
              null;
            const tokenRes = embeddedHostToken
              ? { data: { token: embeddedHostToken } }
              : await getLiveKitToken(code);
            if (cancelled) return;
            const lkToken = tokenRes.data?.token ?? tokenRes.data;
            // Prefer URL from token endpoint (freshest), fall back to session / env
            const lkUrlFromToken = tokenRes.data?.url;
            if (lkUrlFromToken) {
              console.log('[Dashboard] LiveKit URL from token endpoint:', lkUrlFromToken);
              setLivekitUrl(lkUrlFromToken);
            }
            console.log('[Dashboard] LiveKit token acquired');
            setLivekitToken(lkToken);
          } catch (lkErr) {
            // Non-fatal - video will show placeholder
            console.warn('[Dashboard] getLiveKitToken failed (non-fatal):', lkErr.message);
          }
        }

        // 4. Fetch a session-scoped HOST token for the WS handshake.
        // The regular user access token has role=INTERVIEWER which the
        // WsHandshakeInterceptor rejects on the /dashboard endpoint.
        let wsHostToken = null;
        try {
          const htRes = await getHostToken(sessionId);
          if (cancelled) return;
          wsHostToken = htRes.data?.hostToken ?? null;
          console.log('[Dashboard] host token acquired for WS handshake');
        } catch (htErr) {
          console.error('[Dashboard] getHostToken failed:', htErr.message);
        }

        // 5. Open WebSocket for live events (requires the HOST-role token)
        if (wsHostToken) {
          openWs(wsHostToken);
        } else {
          console.error('[Dashboard] cannot open WS - host token missing');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[Dashboard] session load failed:', err.message);
        setError(err.message || 'Failed to load session.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
      wsReconnectSuppressedRef.current = true;
      clearTimeout(wsReconnectTimerRef.current);
      wsReconnectTimerRef.current = null;
      mountedRef.current = false;
      const w = wsRef.current;
      if (w) {
        wsRef.current = null;
        w.onclose = null;
        try { w.close(); } catch { /* ignore */ }
      }
    };
  }, [sessionId, openWs]);

  // ---------------------------------------------------------------------------
  // End session
  // ---------------------------------------------------------------------------

  const handleEndSession = useCallback(async () => {
    if (!window.confirm('End this session? The candidate will be disconnected.')) return;

    setEnding(true);
    console.log('[Dashboard] ending session', sessionId);

    try {
      await endSession(sessionId);
      console.log('[Dashboard] session ended - navigating to report');
    } catch (err) {
      console.error('[Dashboard] endSession failed (proceeding anyway):', err.message);
    } finally {
      // Close WS and clean up regardless. Do NOT clear auth here -
      // ending an interview should keep the host signed in so they
      // land on the candidate's report page, not the login screen.
      wsReconnectSuppressedRef.current = true;
      clearTimeout(wsReconnectTimerRef.current);
      wsReconnectTimerRef.current = null;
      const w = wsRef.current;
      if (w) {
        wsRef.current = null;
        w.onclose = null;
        try { w.close(); } catch { /* ignore */ }
      }
      if (onEndSession) onEndSession();
      navigate(`/report/${sessionId}`);
    }
  }, [sessionId, navigate, onEndSession]);

  // ---------------------------------------------------------------------------
  // Render: loading
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="h-screen bg-gradient-shell flex items-center justify-center text-slate-100">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400 text-sm">Loading session…</p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: error / session ended
  // ---------------------------------------------------------------------------

  if (error || (session && session.status === 'ENDED')) {
    return (
      <div className="h-screen bg-gradient-shell flex items-center justify-center p-6 text-slate-100">
        <div className="text-center space-y-4 max-w-sm">
          <div className="text-4xl" aria-hidden="true">
            {error ? '⚠️' : '✅'}
          </div>
          <h2 className="text-xl font-semibold text-white">
            {error ? 'Failed to load session' : 'This session has ended'}
          </h2>
          <p className="text-slate-400 text-sm">
            {error || 'The interview session is no longer active.'}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 border border-white/10 text-slate-200
                         hover:bg-white/5 text-sm rounded-lg transition-colors"
            >
              Home
            </button>
            <button
              onClick={() => navigate(`/report/${sessionId}`)}
              className="px-4 py-2 bg-gradient-brand hover:opacity-90 text-white
                         text-sm font-semibold rounded-lg shadow-glow transition-opacity"
            >
              View Report
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: live session
  // ---------------------------------------------------------------------------

  const sessionCode    = session?.sessionCode ?? session?.code ?? sessionId;
  const candidateName  = session?.candidateName  || null;
  const candidateEmail = session?.candidateEmail || null;
  const sessionStatus  = session?.status ?? 'ACTIVE';

  const criticalCount = findings.filter(f => (f.severity || '').toUpperCase() === 'CRITICAL').length;
  const highCount     = findings.filter(f => (f.severity || '').toUpperCase() === 'HIGH').length;

  return (
    <div className="h-screen bg-gradient-shell flex flex-col overflow-hidden text-slate-100">

      {/* ── Top header bar ──────────────────────────────────────────────── */}
      <header className="flex items-center justify-between px-4 sm:px-6 py-3
                         glass-dark border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-xl bg-gradient-brand flex items-center justify-center shadow-glow">
            <span className="text-white text-base font-bold">S</span>
          </span>
          <div>
            <h1 className="text-sm font-bold text-white leading-tight tracking-tight">Samastix</h1>
            <p className="text-[11px] text-slate-400 leading-tight flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-400 animate-pulse" />
              Live Session
              <span className="font-mono text-slate-300 ml-0.5">#{sessionCode}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/')}
            className="px-3 py-1.5 text-xs font-medium text-slate-300 hover:text-white
                       border border-white/10 hover:border-white/20 hover:bg-white/5
                       rounded-lg transition-colors"
          >
            Home
          </button>
        </div>
      </header>

      {/* ── Three-column body ────────────────────────────────────────────── */}
      <div className="flex-1 flex overflow-x-auto overflow-y-hidden min-w-0">

        {/* ── LEFT SIDEBAR (~220px) ────────────────────────────────────── */}
        <aside className="w-[240px] shrink-0 glass-dark border-r border-white/10
                          flex flex-col overflow-hidden">

          {/* Session info block */}
          <div className="px-4 pt-5 pb-4 border-b border-white/10 space-y-3">
            <div>
              <p className="text-[9px] font-bold tracking-widest text-slate-500 uppercase mb-1">
                Session Code
              </p>
              <p className="font-mono text-lg font-bold text-gradient tracking-wider">
                {sessionCode}
              </p>
            </div>

            {/* Candidate */}
            <div>
              <p className="text-[9px] font-bold tracking-widest text-slate-500 uppercase mb-1">
                Candidate
              </p>
              {candidateName ? (
                <p className="text-sm text-white font-medium truncate">{candidateName}</p>
              ) : null}
              {candidateEmail ? (
                <p className="text-xs text-slate-400 truncate">{candidateEmail}</p>
              ) : (
                <p className="text-xs text-slate-600 italic">Not joined yet</p>
              )}
            </div>

            {/* Session status badge */}
            <div>
              <p className="text-[9px] font-bold tracking-widest text-slate-500 uppercase mb-1">
                Status
              </p>
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold
                ${sessionStatus === 'ACTIVE'
                  ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                  : 'bg-white/5 text-slate-300 border border-white/10'}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${sessionStatus === 'ACTIVE' ? 'bg-emerald-400 animate-pulse' : 'bg-slate-500'}`} />
                {sessionStatus}
              </span>
            </div>
          </div>

          {/* Agent status */}
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-[9px] font-bold tracking-widest text-slate-500 uppercase mb-2">
              Agent
            </p>
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full shrink-0 ${
                isConnected ? 'bg-emerald-400 animate-pulse' : 'bg-rose-500'
              }`} />
              <span className={`text-xs font-semibold ${
                isConnected ? 'text-emerald-300' : 'text-rose-300'
              }`}>
                {isConnected ? 'Agent Online' : 'Agent Offline'}
              </span>
            </div>
          </div>

          {/* Scan toggle */}
          <div className="px-4 py-4 border-b border-white/10 space-y-2">
            <p className="text-[9px] font-bold tracking-widest text-slate-500 uppercase mb-2">
              Scanner
            </p>
            {/* Both Start + Stop are always rendered side-by-side so the host
                can flip state in one click without waiting for state echo. */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleStartScan}
                disabled={!isConnected || scanActive}
                title={
                  !isConnected ? 'Waiting for agent…'
                  : scanActive ? 'Scan already running'
                  : 'Start the candidate scan'
                }
                className="py-2 text-xs font-semibold rounded-lg
                           bg-emerald-500/90 hover:bg-emerald-400 text-white shadow-soft
                           disabled:opacity-40 disabled:cursor-not-allowed
                           transition-colors flex items-center justify-center gap-1.5"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-white" />
                Start
              </button>
              <button
                onClick={handleStopScan}
                disabled={!scanActive}
                title={scanActive ? 'Stop the candidate scan' : 'Scan is not running'}
                className="py-2 text-xs font-semibold rounded-lg
                           bg-rose-500/90 hover:bg-rose-400 text-white shadow-soft
                           disabled:opacity-40 disabled:cursor-not-allowed
                           transition-colors flex items-center justify-center gap-1.5"
              >
                <span className="w-2 h-2 rounded-sm bg-white" />
                Stop
              </button>
            </div>

            {/* Clear findings - empties the alert list locally and on every
                connected dashboard via cmd_clear_alerts. */}
            <button
              onClick={handleClearFindings}
              disabled={findings.length === 0}
              title={findings.length === 0 ? 'No findings to clear' : 'Clear all findings'}
              className="w-full py-2 text-xs font-semibold rounded-lg
                         border border-white/10 text-slate-300 hover:bg-white/5
                         disabled:opacity-40 disabled:cursor-not-allowed
                         transition-colors flex items-center justify-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                   strokeLinejoin="round" aria-hidden="true">
                <path d="M3 6h18" />
                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <path d="m19 6-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              </svg>
              Clear findings
            </button>

            <div className="flex items-center gap-2 pt-0.5">
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                scanActive ? 'bg-emerald-400 animate-pulse' : 'bg-slate-600'
              }`} />
              <span className="text-[10px] text-slate-500">
                {scanActive
                  ? 'Scanning…'
                  : !isConnected
                  ? 'No agent'
                  : 'Idle'}
              </span>
            </div>
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-3 divide-x divide-white/10 border-b border-white/10">
            <QuickStat value={criticalCount}    label="Crit"  color="text-rose-300" />
            <QuickStat value={highCount}        label="High"  color="text-amber-300" />
            <QuickStat value={findings.length}  label="Total" color="text-slate-200" />
          </div>

          {/* Spacer pushes end session button to bottom */}
          <div className="flex-1" />

          {/* End Session - pinned at bottom */}
          <div className="p-4 border-t border-white/10">
            <button
              onClick={handleEndSession}
              disabled={ending}
              className="w-full py-2 text-xs font-semibold rounded-lg
                         border border-rose-500/40 text-rose-300 hover:bg-rose-500/10
                         disabled:opacity-50 disabled:cursor-not-allowed
                         transition-colors flex items-center justify-center gap-2"
            >
              {ending ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-rose-300 border-t-transparent
                                   rounded-full animate-spin" />
                  Ending…
                </>
              ) : (
                'End Session'
              )}
            </button>
          </div>
        </aside>

        {/* ── CENTER - Tab bar + Video / Code editor ───────────────────── */}
        <main className="flex-1 flex flex-col overflow-hidden bg-[#0b1020] min-w-0">

          {/* Tab bar */}
          <div className="flex items-center border-b border-white/10 shrink-0
                          glass-dark px-2 pt-1 overflow-x-auto no-scrollbar">
            <CenterTab active={activeTab === 'video'} onClick={() => setActiveTab('video')}>
              📹 Video
            </CenterTab>
            <CenterTab active={activeTab === 'code'} onClick={() => setActiveTab('code')}>
              {'</>'} Code
            </CenterTab>
            <CenterTab active={activeTab === 'questions'} onClick={() => setActiveTab('questions')}>
              ❓ Questions
            </CenterTab>
            <CenterTab active={activeTab === 'notes'} onClick={() => setActiveTab('notes')}>
              📝 Notes
            </CenterTab>
            <CenterTab active={activeTab === 'scorecard'} onClick={() => setActiveTab('scorecard')}>
              ⭐ Scorecard
            </CenterTab>
          </div>

          {/* ── VIDEO PANEL - always mounted, hidden when code tab is active ── */}
          <div className={`flex-1 flex flex-col overflow-hidden ${activeTab !== 'video' ? 'hidden' : ''}`}>
            {!livekitToken ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 text-slate-600">
                <div className="w-8 h-8 border-2 border-slate-700 border-t-slate-400
                                rounded-full animate-spin" />
                <p className="text-sm">Connecting to video...</p>
              </div>
            ) : (
              <LiveKitErrorBoundary>
                {/* data-lk-theme="default" activates the built-in LiveKit dark theme.
                    Without it, the control bar buttons render with wrong contrast
                    and are barely visible against the dark background. */}
                <LiveKitRoom
                  serverUrl={livekitUrl}
                  token={livekitToken}
                  connect={true}
                  audio={false}
                  video={false}
                  // Be conservative with bandwidth on the host dashboard.
                  // simulcast lets the SFU pick layers per subscriber.
                  options={{
                    publishDefaults: { simulcast: true },
                    adaptiveStream: true,
                    dynacast: true,
                  }}
                  onMediaDeviceFailure={(failure) => {
                    console.error('[Dashboard] LiveKit media device failure:', failure);
                  }}
                  onError={(err) => {
                    console.error('[Dashboard] LiveKit room error:', err?.message || err);
                  }}
                  data-lk-theme="default"
                  style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                >
                  {/* Reliably publish mic + camera once connected (see component docs). */}
                  <MediaEnabler />
                  <VideoConference />
                  <RoomAudioRenderer />
                </LiveKitRoom>
              </LiveKitErrorBoundary>
            )}
          </div>

          {/* ── CODE EDITOR PANEL - always mounted, hidden when video tab is active ── */}
          <div className={`flex-1 flex flex-col overflow-hidden ${activeTab !== 'code' ? 'hidden' : ''}`}>

            {/* Toolbar */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10
                            shrink-0 glass-dark flex-wrap">

              {/* Language selector */}
              <select
                value={language}
                onChange={handleLanguageChange}
                className="bg-white/5 border border-white/10 text-white text-xs
                           rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1
                           focus:ring-brand-500 cursor-pointer"
              >
                {LANGUAGE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value} className="bg-slate-900">{opt.label}</option>
                ))}
              </select>

              {/* Run button */}
              <button
                onClick={handleRun}
                disabled={executing}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs
                           font-semibold bg-emerald-500 hover:bg-emerald-400 text-white shadow-soft
                           disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {executing ? (
                  <>
                    <span className="w-3 h-3 border-2 border-white border-t-transparent
                                     rounded-full animate-spin" />
                    Running…
                  </>
                ) : (
                  '▶ Run'
                )}
              </button>

              {/* Save button */}
              <button
                onClick={handleSave}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs
                           font-semibold border border-white/10 text-slate-200
                           hover:bg-white/5 transition-colors min-w-[60px] justify-center"
              >
                {saveSuccess ? '✓ Saved' : 'Save'}
              </button>

              {/* Right-side metadata */}
              <div className="ml-auto flex items-center gap-4 text-[10px] text-slate-500">
                {lastSavedAt && (
                  <span>
                    Last saved:{' '}
                    <span className="text-slate-400 font-mono">
                      {lastSavedAt.toLocaleTimeString('en-GB', {
                        hour:   '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                    </span>
                  </span>
                )}
                {lastSavedBy && (
                  <span>
                    Edited by: <span className="text-slate-300">{lastSavedBy}</span>
                  </span>
                )}
              </div>
            </div>

            {/* Monaco Editor - fills remaining height */}
            <div className="flex-1 overflow-hidden">
              <Editor
                height="100%"
                language={language}
                value={content}
                onChange={(val) => {
                  const v = val ?? '';
                  setContent(v);
                  contentRef.current = v;
                  // Live content sync now flows through Yjs (MonacoBinding ->
                  // ydoc.on('update') -> sendWsPayload({event:'yjs_update'})).
                  // We only persist to the DB on a longer debounce here.
                  debouncedSave(v, languageRef.current);
                }}
                onMount={handleEditorMount}
                theme="vs-dark"
                options={{
                  fontSize:             14,
                  minimap:              { enabled: false },
                  scrollBeyondLastLine: false,
                  wordWrap:             'on',
                  lineNumbers:          'on',
                  renderLineHighlight:  'line',
                  tabSize:              4,
                }}
              />
            </div>

            {/* Output panel - collapsible, shown after first Run */}
            {outputOpen && (
              <div className="h-[150px] shrink-0 border-t border-white/10 flex flex-col bg-[#070b18]">

                {/* Output panel header */}
                <div className="flex items-center justify-between px-3 py-1.5
                                border-b border-white/10 shrink-0">
                  <div className="flex items-center gap-4">
                    <span className="text-[9px] font-bold tracking-widest text-slate-400 uppercase">
                      Execution Result
                    </span>
                    {execResult?.status && (
                      <span className={`text-[9px] font-bold uppercase tracking-wide ${
                        execResult.status === 'OK' || execResult.status === 'SUCCESS'
                          ? 'text-emerald-400'
                          : 'text-rose-400'
                      }`}>
                        {execResult.status}
                      </span>
                    )}
                    {execResult?.time != null && (
                      <span className="text-[10px] text-slate-500">{execResult.time}s</span>
                    )}
                    {execResult?.memory != null && (
                      <span className="text-[10px] text-slate-500">{execResult.memory} KB</span>
                    )}
                  </div>
                  <button
                    onClick={() => setOutputOpen(false)}
                    className="text-slate-500 hover:text-white text-base leading-none
                               px-1 transition-colors"
                    aria-label="Close output panel"
                  >
                    ×
                  </button>
                </div>

                {/* stdout + stderr */}
                {execResult ? (
                  <div className="flex-1 overflow-hidden flex">

                    {/* stdout */}
                    <div className="flex-1 flex flex-col overflow-hidden border-r border-white/10">
                      <p className="text-[9px] font-bold tracking-widest text-slate-500
                                    uppercase px-3 pt-1.5 pb-1 shrink-0">
                        Output
                      </p>
                      <pre className="flex-1 overflow-y-auto px-3 pb-2 text-[11px]
                                      text-emerald-300 font-mono leading-relaxed
                                      whitespace-pre-wrap break-words">
                        {execResult.stdout
                          ? execResult.stdout
                          : <span className="text-slate-600 italic">no output</span>
                        }
                      </pre>
                    </div>

                    {/* stderr / compile output */}
                    <div className="flex-1 flex flex-col overflow-hidden">
                      <p className="text-[9px] font-bold tracking-widest text-slate-500
                                    uppercase px-3 pt-1.5 pb-1 shrink-0">
                        Errors
                      </p>
                      <pre className="flex-1 overflow-y-auto px-3 pb-2 text-[11px]
                                      text-rose-300 font-mono leading-relaxed
                                      whitespace-pre-wrap break-words">
                        {(execResult.stderr || execResult.compileOutput)
                          ? (execResult.stderr || execResult.compileOutput)
                          : <span className="text-slate-600 italic">no errors</span>
                        }
                      </pre>
                    </div>

                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <p className="text-xs text-slate-500">Run code to see output</p>
                  </div>
                )}
              </div>
            )}

          </div>

          {/* ── Phase 1 - Questions / Notes / Scorecard panels ─────────── */}
          <div className={`flex-1 flex flex-col overflow-hidden ${activeTab !== 'questions' ? 'hidden' : ''}`}>
            <QuestionsTab sessionId={sessionId} lastChangeToken={questionChangeToken} />
          </div>
          <div className={`flex-1 flex flex-col overflow-hidden ${activeTab !== 'notes' ? 'hidden' : ''}`}>
            <NotesTab
              sessionId={sessionId}
              initial={session?.notes}
              sessionStart={session?.startedAt || session?.createdAt}
            />
          </div>
          <div className={`flex-1 flex flex-col overflow-hidden ${activeTab !== 'scorecard' ? 'hidden' : ''}`}>
            <ScorecardTab sessionId={sessionId} initial={session?.scorecard} />
          </div>
        </main>

        {/* ── RIGHT PANEL - Findings feed (~300px) ─────────────────────── */}
        <aside className="w-[320px] shrink-0 glass-dark border-l border-white/10
                          flex flex-col overflow-hidden">

          {/* Panel header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
            <p className="text-xs font-bold tracking-widest text-slate-300 uppercase">
              Findings
            </p>
            {findings.length > 0 && (
              <span className="bg-gradient-brand text-white text-[10px] font-bold
                               px-2 py-0.5 rounded-full min-w-[20px] text-center shadow-glow">
                {findings.length > 99 ? '99+' : findings.length}
              </span>
            )}
          </div>

          {/* I-P0-B - server integrity status (not a score, not a verdict) */}
          <div className="px-3 py-3 border-b border-white/10 shrink-0">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Integrity status</p>
            <IntegrityStatusBadge status={integrityStatus} />
            {monitoring === 'INTERRUPTED' && (
              <p role="status" className="mt-2 text-[11px] text-amber-300">
                Integrity monitoring interrupted (no agent heartbeat). This is a technical state, not a candidate signal.
              </p>
            )}
            {monitoring === 'RESTORED' && (
              <p role="status" className="mt-2 text-[11px] text-emerald-300">Integrity monitoring restored.</p>
            )}
            {agentHealth && (
              <p className="mt-1 text-[10px] text-slate-500">
                Agent {agentHealth.agentVersion || ''} · process scanner {agentHealth.detectors?.processScanner ? 'on' : 'off'}
              </p>
            )}
            <button
              type="button"
              disabled={releasing}
              onClick={async () => {
                if (!window.confirm('Reset the candidate device? Use this only if the candidate must switch computers. '
                  + 'The current agent connection will be closed.')) return;
                setReleasing(true);
                try { await releaseAgentBinding(sessionId); } catch (e) { console.warn('[Dashboard] release failed', e?.message); }
                finally { setReleasing(false); }
              }}
              className="mt-2 text-[10px] text-slate-400 underline hover:text-slate-200 disabled:opacity-50"
            >
              Reset candidate device
            </button>
          </div>

          {/* Phase 1 - readiness summary */}
          <div className="px-3 py-3 border-b border-white/10 shrink-0">
            <CandidateReadinessCard readiness={readiness} />
          </div>

          {/* Scrollable findings list */}
          <div className="flex-1 overflow-y-auto">
            {findings.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-2 p-6 text-center">
                <span className="text-3xl" aria-hidden="true">
                  {scanActive ? '🔍' : '🛑'}
                </span>
                <p className="text-slate-400 text-xs leading-relaxed">
                  {scanActive
                    ? 'No findings yet. Scan is monitoring...'
                    : 'Start scan to begin monitoring.'}
                </p>
              </div>
            ) : (
              findings.map((f, i) => (
                <FindingCard key={f.id ?? i} finding={f} />
              ))
            )}
          </div>

          {/* Footer link to full report */}
          <div className="p-3 border-t border-white/10 shrink-0">
            <button
              onClick={() => navigate(`/report/${sessionId}`)}
              className="w-full py-1.5 text-xs text-slate-300 hover:text-brand-300
                         transition-colors text-center font-medium"
            >
              View Full Report →
            </button>
          </div>
        </aside>

      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function QuickStat({ value, label, color }) {
  return (
    <div className="py-3 text-center">
      <div className={`text-lg font-bold ${color}`}>{value}</div>
      <div className="text-[9px] text-slate-500 mt-0.5 uppercase tracking-wider font-semibold">{label}</div>
    </div>
  );
}

/**
 * CenterTab - tab button for the Video / Code tab bar in the center column.
 * Keeps a bottom-border highlight on the active tab; inactive tabs fade in on hover.
 */
function CenterTab({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`relative px-4 py-2.5 text-xs font-semibold transition-colors whitespace-nowrap
                  ${active
                    ? 'text-white'
                    : 'text-slate-400 hover:text-slate-200'}`}
    >
      {children}
      {active && (
        <span className="absolute left-2 right-2 -bottom-px h-0.5 bg-gradient-brand rounded-full" />
      )}
    </button>
  );
}

